"use client";

import { useEffect, useState, useMemo, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { PageLayout } from "@/components/ui/PageLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import Avatar from "boring-avatars";

// ── Types ────────────────────────────────────────────────────────────────────

interface Stablebond {
  id: string;
  symbol: string;
  name: string;
  issuer: string;
  totalSupply: number;
  numHolders: number;
  decimals: number;
  lastUpdated: string;
}

interface StablebondsResponse {
  assets: Stablebond[];
  count: number;
}

type SortKey = "symbol" | "totalSupply" | "numHolders" | "lastUpdated";
interface SortConfig { key: SortKey | null; direction: "asc" | "desc" | null; }

// ── Metadata ─────────────────────────────────────────────────────────────────

const BOND_META: Record<string, { description: string; country: string }> = {
  CETES:   { description: "Mexican Government T-Bills", country: "MX" },
  EUROB:   { description: "Euro-denominated Government Bonds", country: "EU" },
  KTB:     { description: "Korean Treasury Bonds", country: "KR" },
  TESOURO: { description: "Brazilian Treasury Bonds", country: "BR" },
  USTRY:   { description: "US Treasury Bonds", country: "US" },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatSupply(supply: number): string {
  if (supply >= 1_000_000_000) return `${(supply / 1_000_000_000).toFixed(2)}B`;
  if (supply >= 1_000_000) return `${(supply / 1_000_000).toFixed(2)}M`;
  if (supply >= 1_000) return `${(supply / 1_000).toFixed(2)}K`;
  return supply.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ── Logo Resolution (same pattern as TokenTableParts.tsx:160-180) ────────────

function BondLogo({ bond, logos, size = 28 }: { bond: Stablebond; logos: Record<string, string>; size?: number }) {
  // Key format in manifest: SYMBOL-ISSUER (dash-separated)
  const manifestKey = bond.id.replace(/:/g, "-");
  const src = logos[manifestKey] || logos[bond.id] || null;

  if (src) {
    return (
      <div className="tokenSymbolLabel overflow-hidden rounded-full" style={{ width: size, height: size }}>
        <Image src={src} alt={`${bond.symbol} logo`} width={size} height={size} className="object-cover" unoptimized />
      </div>
    );
  }
  return (
    <div className="tokenSymbolLabel overflow-hidden rounded-full" style={{ width: size, height: size }}>
      <Avatar size={size} name={bond.id} variant="beam" colors={["#1a1a2e", "#16213e", "#0f3460", "#533483", "#e94560"]} />
    </div>
  );
}

// ── Top Widgets (bonds only) ─────────────────────────────────────────────────

function TopBonds({ bonds }: { bonds: Stablebond[] }) {
  const topBySupply = useMemo(() => [...bonds].sort((a, b) => b.totalSupply - a.totalSupply).slice(0, 5), [bonds]);
  const topByHolders = useMemo(() => [...bonds].sort((a, b) => b.numHolders - a.numHolders).slice(0, 5), [bonds]);
  const topByRecent = useMemo(() => [...bonds].sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()).slice(0, 5), [bonds]);

  const rankColor = (i: number) => i === 0 ? "#c8ff5e" : i === 1 ? "#a0a0a0" : "#8a6a4a";

  return (
    <div className="topTokensOverview">
      <div className="transform-gpu transition-transform hover:scale-[1.01] anim-fadeSlideInUp-300ms" style={{ animationDelay: "0.05s" }}>
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            HIGHEST SUPPLY
          </div>
          {topBySupply.map((b, i) => (
            <div key={b.id} className="stat-row">
              <span className="stat-rank" style={{ color: rankColor(i) }}>#{i + 1}</span>
              <span className="stat-pair">{b.symbol}</span>
              <span className="val-muted">{formatSupply(b.totalSupply)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="transform-gpu transition-transform hover:scale-[1.01] anim-fadeSlideInUp-300ms" style={{ animationDelay: "0.1s" }}>
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            MOST HOLDERS
          </div>
          {topByHolders.map((b, i) => (
            <div key={b.id} className="stat-row">
              <span className="stat-rank" style={{ color: rankColor(i) }}>#{i + 1}</span>
              <span className="stat-pair">{b.symbol}</span>
              <span className="val-muted">{b.numHolders.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="transform-gpu transition-transform hover:scale-[1.01] anim-fadeSlideInUp-300ms" style={{ animationDelay: "0.15s" }}>
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            RECENTLY UPDATED
          </div>
          {topByRecent.map((b, i) => (
            <div key={b.id} className="stat-row">
              <span className="stat-rank" style={{ color: rankColor(i) }}>#{i + 1}</span>
              <span className="stat-pair">{b.symbol}</span>
              <span className="val-muted">{b.lastUpdated ? formatDate(b.lastUpdated) : "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function BondsPage() {
  const router = useRouter();
  const [data, setData] = useState<StablebondsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logos, setLogos] = useState<Record<string, string>>({});

  // Table state — matches TokenTable/PoolsTable patterns
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });

  useEffect(() => {
    const baseUri = process.env.NEXT_PUBLIC_BASE_DATA_URI ?? "https://api-dev.hoops.fi";
    fetch(`${baseUri}/stablebonds`)
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${String(res.status)}`); return res.json() as Promise<StablebondsResponse>; })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "Unknown error"); setLoading(false); });
  }, []);

  // Load logos manifest
  useEffect(() => {
    fetch("/token-logos.json")
      .then((res) => res.json())
      .then((d: Record<string, unknown>) => {
        const logos = (d.logos ?? d) as Record<string, string>;
        setLogos(logos);
      })
      .catch(() => { /* ignore logo load failures */ });
  }, []);

  const bonds = data?.assets ?? [];

  // Search
  const filtered = useMemo(() => {
    if (!searchQuery) return bonds;
    const q = searchQuery.toLowerCase();
    return bonds.filter((b) => {
      const meta = BOND_META[b.symbol];
      return b.symbol.toLowerCase().includes(q) ||
        b.name.toLowerCase().includes(q) ||
        (meta !== undefined && meta.description.toLowerCase().includes(q));
    });
  }, [bonds, searchQuery]);

  // Sort — 3-state toggle matching PoolsTable
  const handleSort = (key: SortKey) => {
    setSortConfig((current) => {
      if (current.key === key) {
        if (current.direction === "asc") return { key, direction: "desc" };
        if (current.direction === "desc") return { key: null, direction: null };
      }
      return { key, direction: "asc" };
    });
  };

  const sorted = useMemo(() => {
    const d = [...filtered];
    const { key, direction } = sortConfig;
    if (!key || !direction) return d;
    const sign = direction === "asc" ? 1 : -1;
    d.sort((a, b) => {
      switch (key) {
        case "symbol": return a.symbol.localeCompare(b.symbol) * sign;
        case "totalSupply": return (a.totalSupply - b.totalSupply) * sign;
        case "numHolders": return ((a.numHolders ?? 0) - (b.numHolders ?? 0)) * sign;
        case "lastUpdated": return (new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime()) * sign;
        default: return 0;
      }
    });
    return d;
  }, [filtered, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sorted.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginated = sorted.slice(startIndex, startIndex + entriesPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, entriesPerPage]);

  // SortableHeader — exact pattern from PoolsTable
  const SortableHeader = ({ children, sortKey, align = "left" }: { children: ReactNode; sortKey: SortKey; align?: "left" | "right" }) => (
    <TableHead onClick={() => { handleSort(sortKey); }} className={cn("cursor-pointer select-none", align === "right" ? "text-right" : "text-left")}>
      <div className="flex items-center gap-1" style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
        <span>{children}</span>
        <ArrowUpDown className={`h-3 w-3 ${sortConfig.key === sortKey ? "text-foreground" : "text-muted-foreground/50"}`} />
      </div>
    </TableHead>
  );

  function handleRowClick(bond: Stablebond) {
    // Navigate using dash-separated format matching token route convention
    const tokenPath = `${bond.symbol}-${bond.issuer}`;
    router.push(`/tokens/${tokenPath}`);
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="anim-fadeSlideInLeft-500ms">
          <div className="flex items-baseline gap-3">
            <h1 className="text-[28px] font-normal font-serif leading-tight">Stablebonds</h1>
            {data && (
              <span className="text-[11px] tabular-nums px-2 py-[3px] rounded-full"
                style={{ background: "#141414", color: "var(--text-secondary)", border: "1px solid #222" }}>
                {data.count} assets
              </span>
            )}
          </div>
          <p className="text-[11px] tracking-[0.04em] mt-1" style={{ color: "var(--text-secondary)" }}>
            Etherfuse tokenized government bonds on Stellar
          </p>
        </div>

        {loading && <div className="table-wrap animate-pulse" style={{ height: 300 }} />}

        {error && (
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl" style={{ background: "#1a0d0d", border: "1px solid #3a1515" }} role="alert">
            <p className="text-[12px] font-medium" style={{ color: "#e05252" }}>Failed to load stablebonds data: {error}</p>
          </div>
        )}

        {!loading && !error && bonds.length === 0 && (
          <div className="flex items-center justify-center py-16 rounded-xl" style={{ background: "var(--surface)", border: "1px solid #1e1e1e" }}>
            <p className="text-[13px]" style={{ color: "var(--text-secondary)" }}>No stablebond data available yet.</p>
          </div>
        )}

        {!loading && !error && bonds.length > 0 && (
          <div className="space-y-5">
            {/* Top widgets */}
            <TopBonds bonds={bonds} />

            {/* Search bar */}
            <div className="filter-bar">
              <div className="search-wrap">
                <svg className="search-icon-pos" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.3-4.3" />
                </svg>
                <input className="search-input" placeholder="Search bonds..." value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); }} />
              </div>
              {searchQuery && <button className="pill" onClick={() => { setSearchQuery(""); }}>RESET</button>}
            </div>

            {/* Table — shadcn primitives matching /tokens design */}
            <div className="table-wrap">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader sortKey="symbol">Asset</SortableHeader>
                    <TableHead>Description</TableHead>
                    <TableHead>Country</TableHead>
                    <SortableHeader sortKey="totalSupply" align="right">Total Supply</SortableHeader>
                    <SortableHeader sortKey="numHolders" align="right">Holders</SortableHeader>
                    <SortableHeader sortKey="lastUpdated" align="right">Last Updated</SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="table-cell-base">No bonds found</TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((bond) => {
                      const meta = BOND_META[bond.symbol] ?? { description: bond.name, country: "—" };
                      return (
                        <TableRow key={bond.id} className="group table-row-hover" onClick={() => { handleRowClick(bond); }}>
                          <TableCell>
                            <div className="tokenSymbolContainer">
                              <BondLogo bond={bond} logos={logos} size={28} />
                              <div>
                                <div className="tokenSymbolTitle">{bond.symbol}</div>
                                <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{bond.name}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell style={{ color: "var(--text-secondary)" }}>{meta.description}</TableCell>
                          <TableCell>
                            <span className="text-[10px] tracking-[0.06em] px-[6px] py-[2px] rounded"
                              style={{ background: "#1a1a1a", color: "#666", border: "1px solid #222" }}>
                              {meta.country}
                            </span>
                          </TableCell>
                          <TableCell className="text-right" style={{ color: "var(--text-primary)" }}>{formatSupply(bond.totalSupply)}</TableCell>
                          <TableCell className="text-right" style={{ color: "var(--text-primary)" }}>{bond.numHolders?.toLocaleString() ?? "—"}</TableCell>
                          <TableCell className="text-right" style={{ color: "var(--text-secondary)" }}>{bond.lastUpdated ? formatDate(bond.lastUpdated) : "—"}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>

              {/* Pagination — same pattern as PoolsTable */}
              {sorted.length > entriesPerPage && (
                <div className="table-footer">
                  <div className="table-footer-left">
                    <div className="table-footer-entries">
                      <span>Show</span>
                      <Select value={entriesPerPage.toString()} onValueChange={(v) => { setEntriesPerPage(parseInt(v, 10)); setCurrentPage(1); }}>
                        <SelectTrigger className="h-auto w-auto px-[8px] py-[3px] text-[10px] rounded-[var(--r)] shadow-none"
                          style={{ background: "var(--surface-hi)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }}>
                          <SelectValue placeholder="10" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                        </SelectContent>
                      </Select>
                      <span>entries</span>
                    </div>
                    <span>Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, sorted.length)} of {sorted.length} entries</span>
                  </div>
                  <div className="table-footer-right">
                    <button className="btn-sm" onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); }} disabled={currentPage === 1}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button key={page} className={`page-num${currentPage === page ? " page-num-active" : ""}`} onClick={() => { setCurrentPage(page); }}>{page}</button>
                    ))}
                    <button className="btn-sm" onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); }} disabled={currentPage === totalPages}>
                      Next
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <p className="text-[10px]" style={{ color: "#444" }}>
              Issuer: {bonds[0]?.issuer ? `${bonds[0].issuer.slice(0, 10)}...${bonds[0].issuer.slice(-4)}` : "—"} (Etherfuse)
            </p>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
